import React from 'react';
import NumberFormat from 'react-number-format';
import { formatDate } from '../../../utils/shared.utils';
import { stripReturnNotePrefix } from '../../../constants/product-return';
import { RECEIPT_BUSINESS_NAME, RECEIPT_SEAL_IMAGE_URL } from '../../../constants/receipt';

/** `GET mongol/return/:returnId`-ийн `header` — нэг буцаалтын баримт (нэг нийлүүлэгч). */
export type ReturnReceiptHeader = {
  returnId: number;
  createdAt?: string | null;
  createdBy?: string | null;
  providerId?: number | null;
  providerName?: string | null;
  providerEmail?: string | null;
  providerPhone?: string | null;
  providerRegister?: string | null;
  providerTown?: string | null;
  providerDistrict?: string | null;
  providerUnit?: string | null;
  providerStreet?: string | null;
};

/** `GET mongol/return/:returnId`-ийн `rows` — буцаасан бараа × өртгийн мөр бүр. */
export type ReturnReceiptRow = {
  id: number;
  historyId: number;
  /** Буцаалтын дараах агуулахын үлдэгдэл */
  countAfter?: number | null;
  productId: number;
  productName: string;
  productSlug?: string | null;
  productSize?: number | null;
  productUnit?: string | null;
  minusCount: number;
  note?: string | null;
  /** Хасахын өмнөх өртгийн мөрийн үлдэгдэл */
  warehouseImportCount?: number | null;
  importId?: number | null;
  importCost?: number | null;
  importExpireDate?: string | null;
};

export type ReturnReceiptData = {
  header: ReturnReceiptHeader;
  rows: ReturnReceiptRow[];
};

export function returnLineAmount(row: ReturnReceiptRow): number {
  return (Number(row.importCost) || 0) * (Number(row.minusCount) || 0);
}

export function returnReceiptTotals(rows: ReturnReceiptRow[]): { count: number; amount: number } {
  return rows.reduce(
    (totals, row) => ({
      count: totals.count + (Number(row.minusCount) || 0),
      amount: totals.amount + returnLineAmount(row),
    }),
    { count: 0, amount: 0 }
  );
}

function productLabel(row: ReturnReceiptRow): string {
  return row.productSize
    ? `${row.productName} - ${row.productSize} ${row.productUnit || ''}`.trim()
    : row.productName;
}

function lineDetails(row: ReturnReceiptRow): { note: string; expireDate: string } {
  return {
    note: stripReturnNotePrefix(row.note),
    expireDate: row.importExpireDate ? String(row.importExpireDate).slice(0, 10) : '',
  };
}

function providerAddress(header: ReturnReceiptHeader): string {
  return [header.providerTown, header.providerDistrict, header.providerUnit, header.providerStreet]
    .filter((part) => part && String(part).trim())
    .join(', ');
}

function Money({ value }: { value: number }) {
  return <NumberFormat displayType="text" value={value} suffix="₮" thousandSeparator={true} decimalScale={2} />;
}

/**
 * Буцаалтын баримт — хүргэлтийн баримт (DeliveryReceipt2)-тай ижил бүтэцтэй, 2 хувь:
 * нэг нь Шеф Про-д, нөгөө нь барааг хүлээн авсан нийлүүлэгчид үлдэнэ.
 */
function ReturnReceipt({ receipt }: { receipt: ReturnReceiptData }) {
  const { header, rows } = receipt;
  const totals = returnReceiptTotals(rows);
  const address = providerAddress(header);

  return (
    <div className="receipt-container return-receipt">
      {[1, 2].map((copy) => (
        <div className="receipt" key={'return-receipt-' + copy}>
          <h2>Буцаалтын баримт</h2>
          <div className="client-order">
            <div className="business-name">{RECEIPT_BUSINESS_NAME}</div>
            <div className="order-top">
              <div className="order-col order-id fontMain-2">
                <div className="col-top">№</div>
                <div><strong>{header.returnId}</strong></div>
              </div>
              <div className="order-col order-amount">
                <div className="col-top">Нийт дүн</div>
                <div><Money value={totals.amount} /></div>
              </div>
              <div className="order-col">
                <div className="col-top">Огноо</div>
                <div>{header.createdAt ? formatDate(new Date(header.createdAt)) : '–'}</div>
              </div>
            </div>

            <div className="order-items">
              <div className="order-contact">
                <strong>Нийлүүлэгч: </strong>
                {header.providerName || '–'}
              </div>
              {header.providerRegister ? (
                <div className="order-contact">
                  <strong>Регистр: </strong>
                  {header.providerRegister}
                </div>
              ) : null}
              {header.providerPhone ? (
                <div className="order-contact">
                  <strong>Утас: </strong>
                  {header.providerPhone}
                </div>
              ) : null}
              {address ? (
                <div className="order-address">
                  <strong className="col-top">Хаяг: </strong>
                  <span>{address}</span>
                </div>
              ) : null}

              <table>
                <tbody>
                  {rows.map((row) => {
                    const { note, expireDate } = lineDetails(row);
                    return (
                      <React.Fragment key={'return-line-' + row.id}>
                        <tr>
                          <td>{productLabel(row)}</td>
                          <td>
                            {row.importCost != null ? <Money value={Number(row.importCost)} /> : '–'} x {row.minusCount}
                            &nbsp;=&nbsp;
                            <Money value={returnLineAmount(row)} />
                          </td>
                        </tr>
                        {note || expireDate ? (
                          <tr>
                            <td colSpan={2} className="return-line-details">
                              {note ? 'Тайлбар: ' + note : null}
                              {note && expireDate ? ' · ' : null}
                              {expireDate ? <span className="nowrap">Дуусах огноо: {expireDate}</span> : null}
                            </td>
                          </tr>
                        ) : null}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="order-summary">
              <div>Нийт тоо ширхэг: {totals.count}</div>
              <div>Нийт дүн: <Money value={totals.amount} /></div>
              <hr />
              <div className="two-col">
                <div className="signs">
                  <div>Нийт хайрцаг тоо: </div>
                  <br />
                  <div>Хүлээлгэн өгсөн: </div>
                  <br />
                  <div>Хүлээн авсан: </div>
                </div>
                <div className="stamp">
                  <img src={RECEIPT_SEAL_IMAGE_URL} height="70" alt="" />
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default ReturnReceipt;
